import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, Form, InputNumber } from 'tdesign-react';
import styled from 'styled-components';
import { fetchPolices } from '@/slices/namespaceSlice';
import { deal70101, isEmpty } from '@/utils/common';
import { updateRetentionPolicy } from '@/services/namespaceService';
import ColTip from '@/components/Tip';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

const { FormItem } = Form;

function Modal({ visible, handleClose, detail, commonProps, fetchPolices }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  useEffect(() => {
    if (visible) {
      form.reset();
      form.setFieldsValue(detail);
    }
  }, [form, visible, detail]);

  const onCancel = () => {
    handleClose();
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      setSubmitting(true);
      try {
        const { meta } = await updateRetentionPolicy({
          ...commonProps,
          ...fields,
        });
        // 主集群创建的资源已在灾备集群存在
        if (meta.code === 70101) {
          deal70101(meta.publicMessage, () => handleSubmit({ validateResult, fields }), {
            url: '/cluster/namespace/list',
            namespace: commonProps.namespace,
          });

          return;
        }

        await fetchPolices(commonProps);
        handleClose();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Root
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      visible={visible}
      header='消息保留策略'
      width={550}
      onClose={onCancel}
    >
      <Form form={form} onSubmit={handleSubmit} style={{ width: '94%' }} labelWidth={180}>
        <FormItem
          name='retentionTimeInSeconds'
          label={
            <ColTip
              content={
                <>
                  <strong>保留消息时长</strong>
                  <li>1、被所有订阅者都消费并确认的消息保留时长。</li>
                  <li>2、如果存在消息未被确认，即使超过消息保留时长，也不会删除。</li>
                  <li>3、如果该topic没有一个订阅者，认为可删除超过的消息。</li>
                  <li>4、默认值为0，代表可立即标记删除，-1代表永不删除。</li>
                </>
              }
            >
              保留消息时长
            </ColTip>
          }
          rules={[
            { required: true },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='秒' theme='normal' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='retentionSizeInMB'
          label={
            <ColTip
              content={
                <>
                  <strong>保留消息大小</strong>
                  <li>1、被所有订阅者都消费并确认的消息保留大小。</li>
                  <li>2、如果存在消息未被确认，即使超过保留消息大小，也不会删除。</li>
                  <li>3、如果该topic没有一个订阅者，认为可删除超过的消息。</li>
                  <li>4、默认值为0，代表可立即标记删除，-1代表永不删除。</li>
                </>
              }
            >
              保留消息大小
            </ColTip>
          }
          rules={[
            { required: true },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='MB' theme='normal' decimalPlaces={0} />
        </FormItem>

        <FormItem
          name='ttlInSeconds'
          label={
            <ColTip
              content={
                <>
                  <strong>消息过期时长</strong>
                  <li>1、每条消息在每个订阅下的生命周期。</li>
                  <li>2、订阅者未消费的消息存活时长。</li>
                  <li>3、到达消息过期时长后，将自动确定未消费消息。</li>
                  <li>4、默认值为0，代表禁用。</li>
                  <li>5、主要使用场景，避免因长时间未消费导致数据积压无法清除。</li>
                  <p></p>
                  <li>
                    例如配置为1小时，订阅者A能够消费到的数据为最近1小时，当然这取决于自动消息确定的速率。
                  </li>
                </>
              }
            >
              消息过期时长
            </ColTip>
          }
          rules={[
            { required: true },
            { validator: (val) => isEmpty(val) || val >= 0, message: '请输入>=0的值' },
          ]}
        >
          <InputNumber suffix='秒' theme='normal' decimalPlaces={0} />
        </FormItem>
        <FormItem
          name='backlogQuotaSize'
          label={
            <ColTip
              content={
                <>
                  <strong>消息过期大小</strong>
                  <li>1、每条消息在每个订阅下的生命周期。</li>
                  <li>2、订阅者未消费的消息大小。</li>
                  <li>3、到达消息过期大小后，将自动确定未消费消息。</li>
                  <li>4、默认值为-1，代表禁用。</li>
                  <li>5、主要使用场景，避免因未消费导致数据积压无法清除。</li>
                  <p></p>
                  <li>
                    例如配置为1GB，订阅者A能够消费到的数据为最近1GB，当然这取决于自动消息确定的速率。
                  </li>
                </>
              }
            >
              消息过期大小
            </ColTip>
          }
          rules={[
            { required: true },
            { validator: (val) => isEmpty(val) || val >= -1, message: '请输入>=-1的值' },
          ]}
        >
          <InputNumber suffix='MB' theme='normal' decimalPlaces={0} />
        </FormItem>
        {/* <FormItem */}
        {/*  name='backlogRetentionPolicy' */}
        {/*  rules={[{ required: true }]} */}
        {/*  label='Backlog Retention Policy' */}
        {/* > */}
        {/*  <Select options={toTsOptions(options)} clearable /> */}
        {/* </FormItem> */}
      </Form>
    </Root>
  );
}

export default connect(
  (state) => ({
    options: state.dict.backlogRetentionPolicy,
  }),
  {
    fetchPolices,
  },
)(Modal);

const Root = styled(Dialog)`
  .t-input-number {
    width: auto;
    flex: 1;
  }
  .t-input__suffix {
    color: var(--td-font-gray-2);
  }
`;
